import { Modal, Stack, Text, Group, Button, List, ThemeIcon, Alert } from '@mantine/core';
import { AlertTriangle, Clock } from 'react-feather';
import type { CohortCoachWithDetails } from '../../../services/api/cohortCoaches';
import type { GroupWithMembers } from '../../../services/api/groups';
import type { CoachAssignmentWarning } from './groups/coachConflictUtils';

interface AssignCoachConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  coach: CohortCoachWithDetails | null;
  selectedGroupCount: number;
  groupsWithExistingCoach: GroupWithMembers[];
  assignmentWarnings?: CoachAssignmentWarning[];
  onConfirm: () => void;
}

export function AssignCoachConfirmModal({
  opened,
  onClose,
  coach,
  selectedGroupCount,
  groupsWithExistingCoach,
  assignmentWarnings = [],
  onConfirm,
}: AssignCoachConfirmModalProps) {
  if (!coach) return null;

  const coachName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || coach.email;
  const hasReplacements = groupsWithExistingCoach.length > 0;

  const schedulingWarnings = assignmentWarnings.filter((w) => w.type === 'scheduling');
  const interestWarning = assignmentWarnings.find((w) => w.type === 'interest_limit');
  const hasWarnings = assignmentWarnings.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Assign Coach to Groups"
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm">
          Assign <strong>{coachName}</strong> to {selectedGroupCount} group{selectedGroupCount !== 1 ? 's' : ''}?
        </Text>

        {hasReplacements && (
          <Stack gap="xs">
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon size="sm" color="warningAmber" variant="light">
                <AlertTriangle size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500} c="warningAmber">
                This will replace the existing coach on {groupsWithExistingCoach.length} group{groupsWithExistingCoach.length !== 1 ? 's' : ''}:
              </Text>
            </Group>
            <List size="sm" withPadding>
              {groupsWithExistingCoach.map((group) => (
                <List.Item key={group.id}>
                  <Text size="sm">
                    <strong>{group.name}</strong>
                    {group.coach_name && (
                      <Text component="span" c="dimmed"> (currently: {group.coach_name})</Text>
                    )}
                  </Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        )}

        {/* Scheduling conflict warnings */}
        {schedulingWarnings.length > 0 && (
          <Alert
            icon={<Clock size={16} />}
            color="yellow"
            variant="light"
            title="Scheduling Conflicts"
          >
            <Stack gap={4}>
              {schedulingWarnings.map((w) => {
                if (w.type !== 'scheduling') return null;
                return (
                  <Text key={w.groupId} size="sm">
                    <strong>{w.groupName}</strong> — {w.conflicts.length} conflict{w.conflicts.length !== 1 ? 's' : ''}
                  </Text>
                );
              })}
            </Stack>
          </Alert>
        )}

        {/* Interest limit warning */}
        {interestWarning && interestWarning.type === 'interest_limit' && (
          <Alert
            icon={<AlertTriangle size={16} />}
            color="yellow"
            variant="light"
            title="Over Interest Limit"
          >
            <Text size="sm">
              This exceeds <strong>{coachName}</strong>&apos;s interest of {interestWarning.maxGroups} group{interestWarning.maxGroups !== 1 ? 's' : ''} (currently assigned to {interestWarning.currentCount}).
            </Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm} color={hasWarnings ? 'medleyGreen' : undefined}>
            {hasWarnings ? 'Assign Anyway' : 'Assign'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AssignCoachConfirmModal;
